import React, { FC, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { getDataLoadState, mortgage } from '@ally/data-selectors-afg-accounts'
import allytm from '@ally/allytm'
import { Link as MUILink } from '@ally/metronome-ui'
import { LoadState } from '@ally/use-data-domain'
import { HostServices } from '@ally/federated-types'
import { Account, AccountStatus } from '@ally/data-types-afg-accounts'
import { useHostServices } from '../HostServices'
import { Loader } from './Loader'
import { Error as ErrorComponent } from './Error'
import useHiddenForm from '../utils/useHiddenForm'
import AccountSelector from '../utils/AccountSelector'

type SSOResponse = {
  kind: 'SAML'
  url: string
  values: Record<string, string>
}

interface statusType {
  type: 'LOADING' | 'SELECTING' | 'FAILURE'
  loanNumber: string | null
}

interface CenlarSSOOptions {
  hostServices: HostServices
  loanNumber: string
  accessToken: string
  landingPage?: string
}

const sortByStatus = (a: Account, b: Account): number => {
  if (a.status === AccountStatus.ACTIVE && b.status !== AccountStatus.ACTIVE) {
    return -1
  }
  if (a.status !== AccountStatus.ACTIVE && b.status === AccountStatus.ACTIVE) {
    return 1
  }
  return 0
}

const cenlarSSO = async (options: CenlarSSOOptions): Promise<SSOResponse> => {
  const { hostServices, landingPage, loanNumber, accessToken } = options
  const {
    transmitRef,
    log: { track },
    session,
  } = hostServices
  const { data: sessionData } = session
  track('ally-next-remote-sso-mortgage-init', { type: 'SAML' })
  const { getCenlarSaml } = transmitRef

  if (!sessionData) {
    throw Error('NO_SESSION_DATA')
  }

  const result = await getCenlarSaml({
    access_token: accessToken,
    loan_number: loanNumber,
    username: sessionData.userNamePvtEncrypt,
    landing_page: landingPage as any,
  })

  if (result?.type !== 'getCenlarSaml') {
    throw Error('API_ERROR')
  }

  return {
    kind: 'SAML',
    url: result.cenlarSaml.post_url,
    values: {
      SAMLResponse: result.cenlarSaml.saml_assertion,
    },
  }
}

export const HomeLoans: FC = () => {
  const hostServices = useHostServices()
  const {
    log: { track },
    hostData,
    session,
  } = hostServices
  const { HiddenForm, submitForm } = useHiddenForm()

  const mortgageSSOSplitter = hostServices.featureFlags.variation(
    'FF_mortgage-sso-spiltter',
    false,
  )

  const urlParams = new URLSearchParams(window.location.search)
  const { landingPage } = useParams<{ landingPage?: string }>()
  const [status, setStatus] = useState<statusType>({
    type: 'LOADING',
    loanNumber: urlParams.get('id'),
  })

  const triggerError = (reason: string): void => {
    setStatus(current => ({
      ...current,
      type: 'FAILURE',
    }))
    track('ally-next-remote-sso-mortgage-error', {
      reason,
    })
  }

  /**
   * Ignoring optional chaining operator as Jest has issues with coverage
   */
  /* istanbul ignore next */
  const accessToken = session?.data?.access_token

  useEffect(() => {
    if (status.loanNumber && accessToken && status.type !== 'FAILURE') {
      cenlarSSO({
        landingPage,
        loanNumber: status.loanNumber,
        accessToken,
        hostServices,
      })
        .then(response => {
          submitForm(response)
          track('ally-next-remote-sso-mortgage-done', { type: 'SAML' })
        })
        .catch(error => {
          const reason = error?.message ?? 'UNKNOWN_ERROR'
          triggerError(reason)
        })
    }
  }, [landingPage, status, accessToken])

  const accountLoadState = getDataLoadState(hostData)
  const validMortgageAccounts = mortgage
    .getAll(hostData)
    .filter(account => !account.indicator?.blocked)

  useEffect(() => {
    if (status.type !== 'LOADING') return
    if (status.loanNumber) return
    if (accountLoadState === LoadState.FAILED) {
      triggerError('ACCOUNTS_LOAD_FAILED')
      return
    }
    if (accountLoadState !== LoadState.LOADED) return

    // If the user has NO valid account an error is shown
    if (validMortgageAccounts.length === 0) {
      triggerError('NO_VALID_ACCOUNT')
      return
    }

    // If the user only has one valid account, SSO is started with that account
    if (validMortgageAccounts.length === 1) {
      setStatus(current => ({
        ...current,
        loanNumber: validMortgageAccounts[0].accountNumber,
      }))
      return
    }

    track('ally-next-remote-sso-mortgage-multiple-accounts', {
      splitter: mortgageSSOSplitter,
    })
    /**
     * If the mortgage splitter FF is on.
     * They are prompted with a list of their valid accounts.
     * After selecting a valid account the SSO process is started with that account.
     */
    if (mortgageSSOSplitter) {
      // "Page View" Analytics
      allytm.event('pageview', 'AOS:Dashboard:Mortgage Account Selector')
      setStatus(current => ({
        ...current,
        type: 'SELECTING',
      }))
      return
    }

    const firstOpenMortgageAccount = validMortgageAccounts.sort(sortByStatus)[0]

    if (!firstOpenMortgageAccount?.accountNumber) {
      triggerError('NO_VALID_ACCOUNT')
      return
    }

    setStatus(current => ({
      ...current,
      loanNumber: firstOpenMortgageAccount.accountNumber,
    }))
  }, [accountLoadState, status])

  /**
   * Handles submitting the actual SSO request
   */
  const submitSSO = (loanNumber: string): void => {
    setStatus(current => ({
      ...current,
      loanNumber,
      type: 'LOADING',
    }))
  }

  return (
    <>
      {(status.type === 'LOADING' || status.type === 'FAILURE') && (
        <Loader description="We're gathering your home loan information." />
      )}
      {status.type === 'SELECTING' && (
        <AccountSelector
          accounts={validMortgageAccounts}
          onSubmit={submitSSO}
          subTitle="Which home loan account are you here for?"
          identifier="accountNumber"
        />
      )}
      {status.type === 'FAILURE' && (
        <ErrorComponent
          errorMessage={
            <>
              We&apos;re having trouble redirecting you. We&apos;re working on a
              fix, so try again in a bit.{' '}
              <MUILink
                variant="link"
                text="Return to Snapshot"
                allytmln="returnToSnapshot"
              >
                <Link to="/dashboard" />
              </MUILink>
            </>
          }
        />
      )}
      <HiddenForm />
    </>
  )
}
